import type { CalendarView } from './calendarUrlState';
import { fcDateToYmd } from './calendarUrlState';

/** Grille journée et semaine : minuit inclus, minuit suivant exclu. */
export const DAY_START_MIN = 0;
export const DAY_END_MIN = 24 * 60;
export const WEEK_END_MIN = DAY_END_MIN;
/** Première heure collée en haut de la fenêtre au chargement. */
export const CALENDAR_FOCUS_START_MIN = 9 * 60;
/** Hauteur de cette fenêtre : 9h → 19h. */
export const CALENDAR_FOCUS_HOURS = 10;
export const DEFAULT_DURATION_MIN = 60;

export interface TimedSpan {
  id: string;
  startMin: number;
  endMin: number;
}

export interface PlacedSpan extends TimedSpan {
  col: number;
  cols: number;
}

/** Découpe les chevauchements en colonnes (audiences à la même heure). */
export function placeEvents(items: TimedSpan[]): PlacedSpan[] {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const colEnds: number[] = [];
  const placed: PlacedSpan[] = sorted.map((ev) => {
    let col = colEnds.findIndex((end) => end <= ev.startMin);
    if (col < 0) {
      col = colEnds.length;
      colEnds.push(ev.endMin);
    } else {
      colEnds[col] = ev.endMin;
    }
    return { ...ev, col, cols: 1 };
  });
  return placed.map((ev) => {
    const group = placed.filter((o) => o.startMin < ev.endMin && ev.startMin < o.endMin);
    const cols = Math.max(...group.map((o) => o.col)) + 1;
    return { ...ev, cols };
  });
}

export function parseLocalYmd(ymd: string): Date {
  const [ys, ms, ds] = ymd.split('-');
  return new Date(Number(ys), Number(ms) - 1, Number(ds), 12, 0, 0, 0);
}

export function addDaysYmd(ymd: string, days: number): string {
  const d = parseLocalYmd(ymd);
  d.setDate(d.getDate() + days);
  return fcDateToYmd(d);
}

export function startOfWeekMonday(ymd: string): string {
  const d = parseLocalYmd(ymd);
  const mondayOffset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - mondayOffset);
  return fcDateToYmd(d);
}

export function shiftCursor(view: CalendarView, ymd: string, dir: -1 | 1): string {
  if (view === 'day') return addDaysYmd(ymd, dir);
  if (view === 'month' || view === 'planning' || view === 'list') {
    const d = parseLocalYmd(ymd);
    d.setMonth(d.getMonth() + dir);
    return fcDateToYmd(d);
  }
  return addDaysYmd(ymd, dir * 7);
}

export function daysForView(view: CalendarView, ymd: string): string[] {
  if (view === 'day') return [ymd];
  const start = startOfWeekMonday(ymd);
  const count = view === 'week' ? 7 : 0;
  if (count === 0) return [];
  return Array.from({ length: count }, (_, i) => addDaysYmd(start, i));
}

export interface EventClock {
  ymd: string;
  allDay: boolean;
  startMin: number | null;
  endMin: number | null;
}

/** Heure locale. Une date seule (YYYY-MM-DD) est une journée entière. `end` allonge le créneau. */
export function clockOf(start: string, allDay: boolean, end?: string): EventClock {
  if (allDay || /^\d{4}-\d{2}-\d{2}$/.test(start)) {
    return { ymd: start.slice(0, 10), allDay: true, startMin: null, endMin: null };
  }
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) {
    return { ymd: start.slice(0, 10), allDay: true, startMin: null, endMin: null };
  }
  const startMin = d.getHours() * 60 + d.getMinutes();
  const ymd = fcDateToYmd(d);
  let endMin = startMin + DEFAULT_DURATION_MIN;
  if (end) {
    const e = new Date(end);
    if (!Number.isNaN(e.getTime())) {
      if (fcDateToYmd(e) > ymd) endMin = 24 * 60;
      else {
        const candidate = e.getHours() * 60 + e.getMinutes();
        if (candidate > startMin) endMin = candidate;
      }
    }
  }
  return { ymd, allDay: false, startMin, endMin };
}

export function formatHm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TONES = ['sand', 'terra', 'forest', 'sage', 'slate', 'ink'] as const;
export type AgendaTone = (typeof TONES)[number];

export function toneForDossier(dossierId: string | null): AgendaTone {
  if (!dossierId) return 'forest';
  let h = 0;
  for (let i = 0; i < dossierId.length; i++) h = (h + dossierId.charCodeAt(i)) % TONES.length;
  return TONES[h] ?? 'forest';
}
